import { ArrowDownUp, Key, Plus, Search, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../assets/scss/pages/SuperAdminUsers.module.scss';
import SuperAdminLayout from '../../components/superadmin/SuperAdminLayout';
import { useHeaderSlot } from '../../contexts/SuperAdminHeaderContext';
import api from '../../config/axios-conf';
import { useToastContext } from '../../contexts/ToastContext';

// Ô tìm kiếm header giữ state nội bộ → không bị dựng lại element qua setSlot mỗi lần gõ
// (tránh ngắt phiên composition của bộ gõ tiếng Việt làm mất/nhảy chữ).
function HeaderSearchInput({ onSearch, onEnter, placeholder }) {
    const [value, setValue] = useState('');
    const handleChange = (e) => {
        const v = e.target.value;
        setValue(v);
        onSearch(v);
    };
    return (
        <div className={styles.hSearchWrap}>
            <Search size={14} />
            <input
                className={styles.hSearchInput}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
            />
        </div>
    );
}

function UsersManagementContent() {
    const { showSuccess, showError } = useToastContext();
    const { setSlot } = useHeaderSlot();
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [shops, setShops] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedShopId, setSelectedShopId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [resetKeyModal, setResetKeyModal] = useState({ visible: false, key: '', userName: '' });
    const [sortDirection, setSortDirection] = useState('desc');
    const [roleFilter, setRoleFilter] = useState('');
    const [formData, setFormData] = useState({ userName: '', passWord: '', role: 'employee', shopId: '' });

    const fetchShops = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await api.get('/superadmin/shops', { headers: { Authorization: `Bearer ${token}` } });
            setShops(Array.isArray(data) ? data : (data?.data || []));
        } catch (err) {
            showError(err.response?.data?.message || 'Không thể tải danh sách shops');
        }
    }, [showError]);

    const fetchUsers = useCallback(async (shopId = null) => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const url = shopId ? `/users/userAll?shopId=${shopId}` : '/users/userAll';
            const { data } = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setAllUsers(data);
            setUsers(data);
        } catch (err) {
            showError(err.response?.data?.message || 'Không thể tải danh sách users');
        } finally {
            setIsLoading(false);
        }
    }, [showError]);

    const filterUsers = useCallback(() => {
        const term = debouncedSearch.toLowerCase();
        const shopCodeMap = {};
        shops.forEach((shop) => { if (shop.id) shopCodeMap[shop.id] = shop.code?.toString() || ''; });
        let filtered = allUsers.filter((user) => {
            const roleOk = roleFilter ? user.role === roleFilter : true;
            const nameMatch = !term || user.userName?.toLowerCase().includes(term);
            const codeMatch = !term || (shopCodeMap[user.shopId] || '').toLowerCase().includes(term);
            return roleOk && (nameMatch || codeMatch);
        });
        filtered = filtered.sort((a, b) =>
            sortDirection === 'asc' ? (a.id || 0) - (b.id || 0) : (b.id || 0) - (a.id || 0)
        );
        setUsers(filtered);
    }, [debouncedSearch, allUsers, sortDirection, roleFilter, shops]);

    useEffect(() => { fetchShops(); fetchUsers(); }, [fetchShops, fetchUsers]);
    useEffect(() => {
        if (selectedShopId) fetchUsers(selectedShopId);
        else fetchUsers();
    }, [selectedShopId, fetchUsers]);
    useEffect(() => { filterUsers(); }, [filterUsers]);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const searchTermRef = useRef('');
    const handleSearchChange = useCallback((v) => {
        setSearchTerm(v);
        searchTermRef.current = v;
    }, []);
    const handleSearch = useCallback(() => setDebouncedSearch(searchTermRef.current.trim()), []);

    const openCreate = useCallback(() => {
        setEditingUser(null);
        setFormData({ userName: '', passWord: '', role: 'employee', shopId: selectedShopId || '' });
        setShowModal(true);
    }, [selectedShopId]);

    // Header slot
    useEffect(() => {
        const ROLE_LABELS = { '': 'Tất cả role', admin: 'Admin', manage: 'Manage', employee: 'Employee', checkin: 'Checkin' };
        setSlot(
            <div className={styles.headerSlot}>
                <HeaderSearchInput
                    onSearch={handleSearchChange}
                    onEnter={handleSearch}
                    placeholder="Tìm tên đăng nhập, code shop..."
                />
                <button className={styles.hIconBtn} onClick={handleSearch} title="Tìm kiếm">
                    <Search size={15} />
                </button>

                <div className={styles.hDivider} />

                <select
                    className={styles.hSelect}
                    value={selectedShopId}
                    onChange={(e) => setSelectedShopId(e.target.value)}
                    title="Lọc theo shop"
                >
                    <option value="">Tất cả shops</option>
                    {shops.map((shop) => (
                        <option key={shop.id} value={shop.id}>{shop.nameShop} #{shop.code}</option>
                    ))}
                </select>

                <select
                    className={styles.hSelect}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    title="Lọc theo role"
                >
                    {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>

                <button
                    className={styles.hIconBtn}
                    onClick={() => setSortDirection((d) => d === 'desc' ? 'asc' : 'desc')}
                    title={sortDirection === 'desc' ? 'Mới nhất' : 'Cũ nhất'}
                >
                    <ArrowDownUp size={15} />
                </button>

                <div className={styles.hDivider} />

                <button className={styles.hBtnPrimary} onClick={openCreate}>
                    <Plus size={15} /> Tạo mới
                </button>
            </div>
        );
        return () => setSlot(null);
    }, [selectedShopId, roleFilter, sortDirection, shops, handleSearch, handleSearchChange, openCreate, setSlot]);

    const roleBadgeClass = (role) => ({
        admin: styles.badgeAdmin,
        manage: styles.badgeManage,
        employee: styles.badgeEmployee,
        checkin: styles.badgeCheckin,
    }[role] || styles.badgeDefault);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({ userName: user.userName, passWord: '', role: user.role, shopId: user.shopId });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = { userName: formData.userName, role: formData.role };
            if (editingUser) {
                if (formData.passWord) submitData.passWord = formData.passWord;
                await api.put(`/users/${editingUser.id}`, submitData, { headers: { Authorization: `Bearer ${token}` } });
                showSuccess('Cập nhật user thành công!');
            } else {
                if (!formData.passWord) { showError('Mật khẩu là bắt buộc khi tạo user mới'); return; }
                submitData.passWord = formData.passWord;
                await api.post(`/users/signup?shopId=${formData.shopId}`, submitData, { headers: { Authorization: `Bearer ${token}` } });
                showSuccess('Tạo user mới thành công!');
            }
            setShowModal(false);
            setError('');
            fetchUsers(selectedShopId || null);
        } catch (err) {
            const msg = err.response?.data?.message || 'Có lỗi xảy ra';
            setError(msg);
            showError(msg);
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa user này?')) return;
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess('Xóa user thành công!');
            fetchUsers(selectedShopId || null);
        } catch (err) {
            showError(err.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handleGenerateResetKey = async (user) => {
        if (!window.confirm('Sinh mã đổi mật khẩu cho user này? Mã sẽ có hạn 60 phút.')) return;
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        const resetKey = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        try {
            setIsGenerating(true);
            const token = localStorage.getItem('token');
            await api.post(`/superadmin/users/${user.id}/reset-key`, { resetKey, expireMinutes: 60 }, { headers: { Authorization: `Bearer ${token}` } });
            showSuccess(`Đã tạo mã: ${resetKey}`);
            setResetKeyModal({ visible: true, key: resetKey, userName: user.userName });
        } catch (err) {
            showError(err.response?.data?.message || 'Không thể tạo mã đổi mật khẩu');
        } finally {
            setIsGenerating(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner} /> Đang tải...
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.tableCard}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Người dùng</th>
                            <th>Role</th>
                            <th>Shop</th>
                            <th>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr className={styles.emptyRow}>
                                <td colSpan="4">Không có user nào</td>
                            </tr>
                        ) : users.map((user) => {
                            const shop = shops.find((s) => s.id === user.shopId);
                            return (
                                <tr key={user.id}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <span className={styles.userName}>{user.userName}</span>
                                            <span className={styles.userId}>#{user.id}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.badge} ${roleBadgeClass(user.role)}`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td>
                                        {shop ? (
                                            <div className={styles.shopInfo}>
                                                <span className={styles.shopName}>{shop.nameShop}</span>
                                                <span className={styles.shopCode}>#{shop.code}</span>
                                            </div>
                                        ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                                    </td>
                                    <td>
                                        <div className={styles.actions}>
                                            <button className={`${styles.btnSmall} ${styles.btnEdit}`} onClick={() => handleEdit(user)}>Sửa</button>
                                            <button className={`${styles.btnSmall} ${styles.btnInfo}`} onClick={() => handleGenerateResetKey(user)} disabled={isGenerating}>
                                                <Key size={12} /> Tạo mã MK
                                            </button>
                                            <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => handleDelete(user.id)}>
                                                <Trash2 size={12} /> Xóa
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Edit/Create Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>{editingUser ? 'Chỉnh sửa User' : 'Tạo User mới'}</h2>
                            <button className={styles.modalClose} onClick={() => setShowModal(false)}><X /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label>Tên đăng nhập *</label>
                                    <input className={styles.input} name="userName" value={formData.userName} onChange={handleInputChange} required />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>{editingUser ? 'Mật khẩu mới (để trống nếu không đổi)' : 'Mật khẩu *'}</label>
                                    <input className={styles.input} type="password" name="passWord" value={formData.passWord} onChange={handleInputChange} required={!editingUser} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>Role *</label>
                                    <select className={styles.select} name="role" value={formData.role} onChange={handleInputChange} required>
                                        <option value="admin">Admin</option>
                                        <option value="manage">Manage</option>
                                        <option value="employee">Employee</option>
                                        <option value="checkin">Checkin</option>
                                    </select>
                                </div>
                                {!editingUser && (
                                    <div className={styles.formGroup}>
                                        <label>Shop *</label>
                                        <select className={styles.select} name="shopId" value={formData.shopId} onChange={handleInputChange} required>
                                            <option value="">Chọn shop</option>
                                            {shops.map((shop) => (
                                                <option key={shop.id} value={shop.id}>{shop.nameShop} #{shop.code}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.btnCancel} onClick={() => setShowModal(false)}>Hủy</button>
                                <button type="submit" className={styles.btnPrimary}>{editingUser ? 'Cập nhật' : 'Tạo mới'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Key Modal */}
            {resetKeyModal.visible && (
                <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setResetKeyModal({ visible: false, key: '', userName: '' })}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>Mã đổi mật khẩu</h2>
                            <button className={styles.modalClose} onClick={() => setResetKeyModal({ visible: false, key: '', userName: '' })}><X /></button>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={styles.resetKeyUser}>User: <strong>{resetKeyModal.userName}</strong></p>
                            <div className={styles.resetKeyBox}>{resetKeyModal.key}</div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setResetKeyModal({ visible: false, key: '', userName: '' })}>Đóng</button>
                            <button className={styles.btnPrimary} onClick={() => { navigator.clipboard?.writeText(resetKeyModal.key); showSuccess('Đã copy mã'); }}>
                                Copy mã
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function UsersManagement() {
    return (
        <SuperAdminLayout>
            <UsersManagementContent />
        </SuperAdminLayout>
    );
}
